import React, { useState, useEffect } from 'react';
import { ChevronLeft, Loader2, Image, ExternalLink } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { format } from 'date-fns';
import { getBidPhotos } from '../../../utils/api';
import { toast } from 'react-hot-toast';

interface ProgressPhotos {
  reportId: number;
  woNumber: string;
  onBack: () => void;
}

export function ProgressPhotos({ reportId, woNumber, onBack }: ProgressPhotos) {
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<any[]>([]);

  useEffect(() => {
    const loadPhotos = async () => {
      try {
        const allPhotos = await getBidPhotos(reportId);
        console.log(allPhotos);
        
        const unflaggedPhotos = allPhotos.filter(photo => photo.photoType === 'PROGRESS');
        setPhotos(unflaggedPhotos);
      } catch (error) {
        console.error('Error loading unflagged photos:', error);
        toast.error('Failed to load photos');
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [reportId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
        >
          <ChevronLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Progress Photos
          </h2>
          <p className="text-gray-600 dark:text-gray-400">WO #{woNumber}</p>
        </div>
      </div>

      {/* Photos Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
          <Image className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">No unflagged photos available</p>
        </div>
      ) : (
        <PhotoProvider
          photoClosable={false}
          maskOpacity={0.8}
          maskClassName="backdrop-blur-sm"
          loadingElement={<Loader2 className="h-8 w-8 animate-spin" />}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="bg-gray-50 dark:bg-dark-card rounded-lg overflow-hidden border border-light-border dark:border-dark-border"
              >
                <PhotoView 
                  src={photo.url}
                  overlay={
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-3 text-sm backdrop-blur-sm space-y-1">
                      <p>{format(new Date(photo.createdAt), 'MMM d, yyyy HH:mm:ss')}</p>
                      {photo.metadata?.latitude && photo.metadata?.longitude && (
                        <p className="text-gray-300">
                          GPS: {photo.metadata.latitude.toFixed(6)}, {photo.metadata.longitude.toFixed(6)}
                        </p>
                      )}
                    </div>
                  }
                >
                  <img
                    src={photo.url}
                    alt={`Photo ${photo.id}`}
                    className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                </PhotoView>
                <div className="p-2">
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {format(new Date(photo.createdAt), 'MMM d, yyyy HH:mm:ss')}
                    </p>
                    {photo.metadata?.latitude && photo.metadata?.longitude && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        GPS: {photo.metadata.latitude.toFixed(6)}, {photo.metadata.longitude.toFixed(6)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PhotoProvider>
      )}
    </div>
  );
}